import React, { useState, useCallback } from 'react';
import { FieldError } from 'react-hook-form';
import { Eye, EyeOff } from 'lucide-react';
import TextareaAutosize from 'react-textarea-autosize';

const StandardInput: React.FC<{
  register: any;
  id: string;
  label?: string | JSX.Element;
  type: string;
  cb?: Function;
  rows?: number;
  placeholder?: string;
  error?: FieldError;
  disabled?: boolean;
  className?: string;
  inputClassName?: string;
  labelClassName?: string;
  errorClassName?: string;
  handleKeyDown?: (e: React.KeyboardEvent) => void;
}> = ({
  register,
  id,
  label,
  type,
  cb,
  rows,
  placeholder,
  error,
  disabled,
  className = 'w-full',
  inputClassName,
  labelClassName = 'block text-body-sm font-medium text-neutral-700 mb-2',
  errorClassName = 'text-body-sm text-danger-600 mt-1',
  handleKeyDown,
}) => {
  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword((prevState) => !prevState);
  }, []);

  const defaultInputClassName = `w-full px-4 py-3 rounded-input border ${
    error ? 'border-danger-500 focus:ring-danger-500' : 'border-neutral-300 focus:ring-primary-500 focus:border-primary-500'
  } focus:ring-2 text-body text-neutral-900 placeholder:text-neutral-400 disabled:bg-neutral-100 disabled:cursor-not-allowed transition-colors`;

  const finalInputClassName = inputClassName || defaultInputClassName;

  if (type === 'textarea') {
    return (
      <div className={className}>
        {label && (
          <label htmlFor={id} className={labelClassName}>
            {label}
          </label>
        )}
        <TextareaAutosize
          {...register(id)}
          onChange={(e) => {
            register(id).onChange(e);
            if (typeof cb === 'function') {
              cb(e.target.value);
            }
          }}
          id={id}
          minRows={rows || 3}
          placeholder={placeholder}
          className={finalInputClassName}
          aria-invalid={error ? 'true' : 'false'}
          disabled={!!disabled}
          onKeyDown={(e) => !!handleKeyDown && handleKeyDown(e)}
        />
        {error?.message && <p className={errorClassName}>{error.message}</p>}
      </div>
    );
  }

  return (
    <div className={className}>
      {label && (
        <label htmlFor={id} className={labelClassName}>
          {label}
        </label>
      )}
      <div className="relative">
        <input
          {...register(id)}
          onChange={(e) => {
            register(id).onChange(e);
            if (typeof cb === 'function') {
              cb(e.target.value);
            }
          }}
          id={id}
          type={showPassword ? 'text' : type}
          placeholder={placeholder}
          className={finalInputClassName}
          aria-invalid={error ? 'true' : 'false'}
          disabled={!!disabled}
        />

        {type === 'password' && (
          <button
            type="button"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-neutral-700 transition-colors"
            onClick={togglePasswordVisibility}
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        )}
      </div>
      {error?.message && <p className={errorClassName}>{error.message}</p>}
    </div>
  );
};

export default StandardInput;
